import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { TransactionDateFilter } from './TransactionDateFilter';
import TransactionRangeFilter from './TransactionRangeFilter';
import { ReactComponent as SearchIcon } from 'assets/search-icon.svg';
import { ReactComponent as FilterIcon } from 'assets/filter-mobile-icon.svg';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import i18nContext from 'components/i18n-context';
import { getErrorMessageForAlert } from 'services/utils';
import Alert from 'uikit/Alert/Alert';
import {
  TRANSACTION_TYPES,
  CUSTOMER_PERMISSIONS,
  TRANSACTIONS_TABLE_STATUS,
  TOOLTIP_DIRECTION
} from 'components/common/constants';
import { getAccountTitleWithFormatting, getFirstCreatedAccountDate } from 'components/utils';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { FileDownloadButton } from 'uikit/FileDownloadButton/FileDownloadButton';
import 'react-datepicker/dist/react-datepicker.css';
import './LatestTransactions.scss';

const TransactionsFilter = ({
  accountNumber,
  transactionStatuses = [],
  transactionsStore,
  userAccounts,
  userStore,
  isMobile,
  permissions
}) => {
  const i18n = useContext(i18nContext);
  const [isOpen, setIsOpen] = useState(false);
  const [firstCreatedAccountDate, setFirstCreatedAccountDate] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  // Auto-clear error after 10 seconds
  useEffect(() => {
    if (downloadError) {
      const timer = setTimeout(() => {
        setDownloadError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [downloadError]);
  const todayDate = new Date();

  useEffect(() => {
    // Load all wallets including archived ones for filter
    if (accountNumber) {
      userStore.loadAllAccountsForFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountNumber]); // userStore removed from deps to prevent re-runs

  useEffect(() => {
    // Combine active and archived wallets for filters
    const allWallets = [...(userAccounts || []), ...(userStore.archivedWallets || [])];
    if (allWallets.length > 0) {
      setFirstCreatedAccountDate(getFirstCreatedAccountDate(allWallets));
    }
  }, [userAccounts, userStore.archivedWallets]);

  // Combine active and archived wallets for filter options
  const walletsForFilter = [...(userAccounts || []), ...(userStore.archivedWallets || [])];

  const walletsOptions = walletsForFilter?.map((wallet) => {
    const title = getAccountTitleWithFormatting(wallet);

    return {
      key: wallet?.wallet_number,
      value: title,
      currency: wallet?.currency,
      networkCode: wallet?.network
    };
  });

  const transactionTypeOptions = Object.keys(TRANSACTION_TYPES).map((type) => {
    return {
      key: type,
      value: i18n.getMessage(`transaction.type.${TRANSACTION_TYPES[type]}`)
    };
  });

  const currencyOptions = [...new Set(walletsForFilter?.map(({ currency }) => currency))].map((currency) => {
    return {
      key: currency,
      value: currency
    };
  });

  const statusOptions = transactionStatuses?.reduce((acc, status) => {
    if (status !== TRANSACTIONS_TABLE_STATUS.acceptRequest && status !== TRANSACTIONS_TABLE_STATUS.rejectRequest) {
      acc.push({
        key: status,
        value: i18n.getMessage('transaction.status.' + status)
      });
    }
    return acc;
  }, []);

  const handleSetFilter = (name, value) => transactionsStore.setFilter(name, value);

  const handleSetDateFilter = (name, value) => {
    transactionsStore.clearPredefinedFilter();
    transactionsStore.setFilter(name, value);
    if (name === 'from_date' && value > transactionsStore.filters.to_date) {
      transactionsStore.setFilter('to_date', value);
    }
  };

  const handleSearchTest = ({ target: { name, value } }) => {
    handleSetFilter(name, value);
    transactionsStore.getTransactionsWithDebounce(accountNumber);
  };

  const handleSetRangeFilter = (filterType, dateRang) => transactionsStore.setRangeFilter(filterType, dateRang);

  const handleDownloadPDF = async (e) => {
    e.preventDefault();
    setDownloadError(null);
    try {
      await transactionsStore.downloadPDFList(accountNumber);
    } catch (error) {
      const errorMessage = getErrorMessageForAlert(i18n, error, true, null, null);
      setDownloadError(errorMessage || 'Failed to download PDF. Please try again.');
    }
  };

  const handleDownloadCSV = async (e) => {
    e.preventDefault();
    setDownloadError(null);
    try {
      await transactionsStore.downloadCSVList(accountNumber);
    } catch (error) {
      const errorMessage = getErrorMessageForAlert(i18n, error, true, null, null);
      setDownloadError(errorMessage || 'Failed to download CSV. Please try again.');
    }
  };

  // When we don't have permission in account object -> main account
  const disableDownloadReport = permissions
    ? !permissions?.find((permission) => permission === CUSTOMER_PERMISSIONS.CREATE_REPORT)
    : false;

  if (isMobile && !isOpen) {
    return (
      <>
        {/* Error Alert */}
        {downloadError && <Alert type='error' message={downloadError} />}
        <div className={'transaction-filter-compact'}>
          <TransactionRangeFilter
            name={'tabs'}
            value={transactionsStore.rangeFilter}
            onChange={handleSetRangeFilter}
            minDate={firstCreatedAccountDate}
          />
          <div className={'filter-compact-wrapper'}>
            <Input
              className={'search'}
              name={'search_text'}
              value={transactionsStore.filters.search_text && disableDownloadReport}
              onChange={handleSearchTest}
              Icon={SearchIcon}
            />
            <Button className={'filter-button'} type={'primary'} size={'large'} onClick={() => setIsOpen(true)}>
              <FilterIcon />
            </Button>
          </div>
          <FileDownloadButton
            style={'input'}
            isDisabled={disableDownloadReport}
            onClick={handleDownloadPDF}
            type={'DOWNLOAD PDF'}
            tooltipText={disableDownloadReport ? i18n.getMessage('noPermission.downloadFiles') : null}
            tooltipDirection={TOOLTIP_DIRECTION.BOTTOM}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Error Alert */}
      {downloadError && <Alert type='error' message={downloadError} />}
      <form action={'#'} id={'send'} className={'transaction-filter-wrapper'}>
        {isMobile && (
          <div className={'filter-mobile-header'}>
            <h1>{i18n.getMessage('menu.filters.text')}</h1>
            <CloseIcon onClick={() => setIsOpen(false)} className={'filters-close-icon'} />
          </div>
        )}
        <TransactionRangeFilter
          name={'tabs'}
          value={transactionsStore.rangeFilter}
          onChange={handleSetRangeFilter}
          minDate={firstCreatedAccountDate}
        />
        <div className={'filter-wrapper-transactions'}>
          <div className={'filter-wrapper-line'}>
            <TransactionDateFilter
              name={'from_date'}
              value={transactionsStore.filters.from_date}
              onChange={handleSetDateFilter}
              label={i18n.getMessage('transaction.text.from')}
              minDate={firstCreatedAccountDate}
              maxDate={todayDate}
            />
            <TransactionDateFilter
              name={'to_date'}
              value={transactionsStore.filters.to_date}
              onChange={handleSetDateFilter}
              label={i18n.getMessage('transaction.text.to')}
              minDate={
                transactionsStore.filters.from_date ? transactionsStore.filters.from_date : firstCreatedAccountDate
              }
              maxDate={todayDate}
            />
            <Input
              className={'search'}
              name={'search_text'}
              value={transactionsStore.filters.search_text}
              onChange={handleSearchTest}
              Icon={SearchIcon}
            />
            <FileDownloadButton
              style={'input'}
              isDisabled={disableDownloadReport}
              onClick={handleDownloadCSV}
              type={'CSV'}
              tooltipText={disableDownloadReport ? i18n.getMessage('noPermission.downloadFiles') : null}
              tooltipDirection={TOOLTIP_DIRECTION.BOTTOM}
            />
            <FileDownloadButton
              style={'input'}
              isDisabled={disableDownloadReport}
              onClick={handleDownloadPDF}
              type={'PDF'}
              tooltipText={disableDownloadReport ? i18n.getMessage('noPermission.downloadFiles') : null}
              tooltipDirection={TOOLTIP_DIRECTION.BOTTOM}
            />
          </div>
          <div className={'filter-wrapper-line'}>
            <InputDropDown
              className={'wallet'}
              name={'wallets'}
              options={walletsOptions}
              value={transactionsStore.filters.wallets}
              onChange={handleSetFilter}
              isMulti
            />
            <InputDropDown
              className={'transaction-type'}
              name={'types'}
              options={transactionTypeOptions}
              placeholder={i18n.getMessage('transaction.text.transactionType')}
              value={transactionsStore.filters.types}
              onChange={handleSetFilter}
              isMulti
            />
            <InputDropDown
              className={'currency'}
              placeholder={i18n.getMessage('transaction.text.currency')}
              name={'currencies'}
              value={transactionsStore.filters.currencies}
              options={currencyOptions}
              onChange={handleSetFilter}
              isMulti
            />
            <InputDropDown
              className={'status'}
              placeholder={i18n.getMessage('transaction.text.status')}
              name={'statuses'}
              value={transactionsStore.filters.statuses}
              options={statusOptions}
              onChange={handleSetFilter}
              isMulti
            />
            {isMobile && (
              <div className={'filter-buttons-wrapper'}>
                <Button
                  className={'filter-button'}
                  size={'large'}
                  type={'outline'}
                  onClick={() => transactionsStore.clearFilters()}
                >
                  {i18n.getMessage('transaction.clear')}
                </Button>
                <Button className={'filter-button'} size={'large'} onClick={() => setIsOpen(false)}>
                  {i18n.getMessage('transaction.apply')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

TransactionsFilter.propTypes = {
  accountNumber: PropTypes.string,
  transactionStatuses: PropTypes.array,
  transactionsStore: MobXPropTypes.observableObject,
  userAccounts: PropTypes.array,
  userStore: MobXPropTypes.observableObject,
  isMobile: PropTypes.bool,
  permissions: PropTypes.any
};

export default inject((stores) => ({
  userAccounts: stores.userStore.userAccounts,
  accountNumber: stores.userStore.userData.account?.account_number,
  permissions: stores.userStore?.currentCustomerPermissions,
  transactionStatuses: stores.userStore.appFilterStatuses,
  transactionsStore: stores.transactionsStore,
  userStore: stores.userStore
}))(observer(TransactionsFilter));
